import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from '@components/context';
import Input from '@components/legacy/core/Input';
import Button from '@components/legacy/core/Button';

function normalizePositiveIntegerInput(raw) {
    const digits = raw.replace(/\D/g, '');
    if (digits === '') {
        return '';
    }
    return String(parseInt(digits, 10));
}

function isValidLeadTimeDays(value) {
    const days = parseInt(value, 10);
    return Number.isInteger(days) && days >= 1 && days <= 365;
}

const CustomLeadTimePopover = ({ anchorRef, initialValue = '', onApply, onCancel }) => {
    const { t } = useTranslations();
    const popoverRef = useRef(null);
    const inputRef = useRef(null);
    const [value, setValue] = useState(
        initialValue === '' || initialValue == null ? '' : String(initialValue),
    );
    const [position, setPosition] = useState(null);

    const updatePosition = () => {
        if (!anchorRef.current || !popoverRef.current) {
            return;
        }
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const popoverRect = popoverRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = anchorRect.bottom + 4;
        let left = anchorRect.left;

        if (left + popoverRect.width > viewportWidth - 10) {
            left = viewportWidth - popoverRect.width - 10;
        }
        if (left < 10) {
            left = 10;
        }
        if (top + popoverRect.height > viewportHeight - 10) {
            const topAbove = anchorRect.top - popoverRect.height - 4;
            if (topAbove > 10) {
                top = topAbove;
            } else {
                top = Math.max(10, viewportHeight - popoverRect.height - 10);
            }
        }

        setPosition({ top, left });
    };

    useLayoutEffect(() => {
        updatePosition();
    }, [anchorRef]);

    useEffect(() => {
        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                onCancel();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onCancel]);

    useEffect(() => {
        const handleScroll = () => updatePosition();
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll);
        };
    }, [anchorRef]);

    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        }, 0);
        return () => clearTimeout(timer);
    }, []);

    const handleInputChange = (inputValue) => {
        setValue(normalizePositiveIntegerInput(inputValue));
    };

    const handleApply = () => {
        if (!isValidLeadTimeDays(value)) {
            return;
        }
        onApply(parseInt(value, 10));
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            handleApply();
        }
    };

    const isApplyDisabled = !isValidLeadTimeDays(value);

    return createPortal(
        <>
            <div className="recruitment-flow-leadtime-custom-backdrop" onClick={onCancel} />
            <div
                ref={popoverRef}
                className="recruitment-flow-leadtime-custom-popover"
                style={position ? {
                    top: `${position.top}px`,
                    left: `${position.left}px`,
                    visibility: 'visible',
                } : { visibility: 'hidden' }}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="recruitment-flow-leadtime-custom-popover__title">
                    {t('Custom lead time')}
                </div>
                <Input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={value}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                />
                <div className="recruitment-flow-leadtime-custom-popover__actions">
                    <Button
                        type="default"
                        title={t('Cancel')}
                        onClick={onCancel}
                        className="recruitment-flow-leadtime-custom-popover__action-btn"
                    />
                    <Button
                        type="primary"
                        title={t('Apply')}
                        onClick={handleApply}
                        disabled={isApplyDisabled}
                        className="recruitment-flow-leadtime-custom-popover__action-btn"
                    />
                </div>
            </div>
        </>,
        document.body,
    );
};

export default CustomLeadTimePopover;
